import { forwardRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { confirmPasswordReset, requestPasswordReset } from "@/api/auth";
import { ApiError } from "@/api/client";
import { Layout } from "@/components/Layout";
import { NoindexSeo } from "@/components/Seo";

const requestSchema = z.object({
  email: z.string().email("Adresse email invalide"),
});

const confirmSchema = z
  .object({
    password: z.string().min(12, "Mot de passe : au moins 12 caractères"),
    password_confirm: z.string(),
  })
  .refine((data) => data.password === data.password_confirm, {
    message: "Les mots de passe ne correspondent pas",
    path: ["password_confirm"],
  });

type RequestFormData = z.infer<typeof requestSchema>;
type ConfirmFormData = z.infer<typeof confirmSchema>;

export default function PasswordResetPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [formError, setFormError] = useState<string | null>(null);
  const [requestDone, setRequestDone] = useState(false);

  if (token) {
    return <ConfirmForm token={token} />;
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestFormData>({ resolver: zodResolver(requestSchema) });

  const mutation = useMutation({
    mutationFn: (email: string) => requestPasswordReset(email),
    onSuccess: () => setRequestDone(true),
    onError: (err: ApiError) => {
      if (err.code === "RATE_LIMITED") {
        setFormError("Trop de tentatives. Réessayez plus tard.");
      } else {
        setFormError("Une erreur est survenue, veuillez réessayer.");
      }
    },
  });

  const onSubmit = (data: RequestFormData) => {
    setFormError(null);
    mutation.mutate(data.email);
  };

  return (
    <Layout>
      <NoindexSeo title="Réinitialiser le mot de passe" path="/password-reset" />
      <div className="mx-auto max-w-md px-6 py-12">
        <div className="lg-card p-8">
          <h1 className="text-2xl font-bold">Mot de passe oublié</h1>

          {requestDone ? (
            <p className="mt-4 text-gray-700">
              Si un compte existe avec cette adresse, un email de réinitialisation a été envoyé.
              Vérifiez votre boîte de réception (et vos spams).
            </p>
          ) : (
            <>
              <p className="mt-2 text-sm text-gray-600">
                Saisissez votre email pour recevoir un lien de réinitialisation.
              </p>
              <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4" noValidate>
                <Field
                  label="Email"
                  type="email"
                  autoComplete="email"
                  error={errors.email?.message}
                  {...register("email")}
                />

                {formError && (
                  <p role="alert" className="text-sm text-red-700">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="w-full rounded bg-blue-900 px-4 py-2 text-white hover:bg-blue-800 disabled:opacity-50"
                >
                  {mutation.isPending ? "Envoi…" : "Envoyer le lien"}
                </button>
              </form>
            </>
          )}

          <Link to="/login" className="mt-8 inline-block text-sm text-blue-900 underline">
            Retour à la connexion
          </Link>
        </div>
      </div>
    </Layout>
  );
}

function ConfirmForm({ token }: { token: string }) {
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ConfirmFormData>({ resolver: zodResolver(confirmSchema) });

  const mutation = useMutation({
    mutationFn: (data: { password: string }) =>
      confirmPasswordReset(token, data.password),
    onSuccess: () => setDone(true),
    onError: (err: ApiError) => {
      if (err.code === "INVALID_TOKEN") {
        setFormError("Ce lien est invalide ou expiré. Demandez un nouveau lien.");
      } else if (err.code === "PASSWORD_COMPROMISED") {
        setFormError("Ce mot de passe a été compromis dans une fuite. Choisissez-en un autre.");
      } else {
        setFormError("Une erreur est survenue, veuillez réessayer.");
      }
    },
  });

  const onSubmit = (data: ConfirmFormData) => {
    setFormError(null);
    mutation.mutate(data);
  };

  return (
    <Layout>
      <NoindexSeo title="Nouveau mot de passe" path="/password-reset" />
      <div className="mx-auto max-w-md px-6 py-12">
        <div className="lg-card p-8">
          <h1 className="text-2xl font-bold">Nouveau mot de passe</h1>

          {done ? (
            <div className="mt-4 space-y-4">
              <p className="text-green-700">
                Mot de passe modifié avec succès.
              </p>
              <Link
                to="/login"
                className="inline-block rounded bg-blue-900 px-4 py-2 text-white hover:bg-blue-800"
              >
                Se connecter
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4" noValidate>
              <Field
                label="Nouveau mot de passe"
                type="password"
                autoComplete="new-password"
                error={errors.password?.message}
                {...register("password")}
              />
              <Field
                label="Confirmer le mot de passe"
                type="password"
                autoComplete="new-password"
                error={errors.password_confirm?.message}
                {...register("password_confirm")}
              />

              {formError && (
                <p role="alert" className="text-sm text-red-700">
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={mutation.isPending}
                className="w-full rounded bg-blue-900 px-4 py-2 text-white hover:bg-blue-800 disabled:opacity-50"
              >
                {mutation.isPending ? "Modification…" : "Modifier le mot de passe"}
              </button>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
}

type FieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
};

const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, error, ...props },
  ref,
) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <input
        ref={ref}
        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-900 focus:outline-none focus:ring-1 focus:ring-blue-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        {...props}
      />
      {error && <span className="mt-1 block text-sm text-red-700">{error}</span>}
    </label>
  );
});
